import { useEffect, useMemo, useState } from 'react'

import { formatDurationBadge, formatServicePrice } from '@/components/pos/posCatalog'
import { filterCatalogItems } from '@/components/pos/posCatalogSearch'
import { getQuickKeyLabel } from '@/components/pos/posQuickKey'
import { useBranch } from '@/contexts/BranchContext'
import { searchPosServices } from '@/lib/api/pos'
import { toAmount } from '@/lib/api/products'

import {
  getQuickKeyDuration,
  resolveQuickKeyPrice,
} from './posQuickKey'

/**
 * @param {object} props
 * @param {import('./posMockData').PosCatalogMode} props.catalogMode
 * @param {number | null} props.activeCategoryId
 * @param {import('./posCatalog').PosGender} props.pricingGender
 * @param {import('@/types/api').ServiceCategory[]} props.categories
 * @param {import('@/types/api').Service[]} props.services
 * @param {boolean} props.loadingServices
 * @param {string} props.searchQuery
 * @param {import('@/types/api').Product[]} props.products
 * @param {boolean} [props.loadingProducts]
 * @param {import('@/types/api').PosQuickKey[]} [props.quickKeys]
 * @param {boolean} [props.loadingQuickKeys]
 * @param {(service: import('@/types/api').Service) => void} [props.onServiceSelect]
 * @param {(product: import('@/types/api').Product) => void} [props.onProductSelect]
 * @param {(key: import('@/types/api').PosQuickKey) => void} [props.onQuickKeySelect]
 * @param {boolean} [props.disabled]
 */
export function PosCatalogPicker({
  catalogMode,
  activeCategoryId,
  pricingGender,
  categories,
  services,
  loadingServices,
  searchQuery,
  products = [],
  loadingProducts = false,
  quickKeys = [],
  loadingQuickKeys = false,
  onServiceSelect,
  onProductSelect,
  onQuickKeySelect,
  disabled = false,
}) {
  const { activeBranchId } = useBranch()
  const [searchResults, setSearchResults] = useState([])
  const [loadingSearch, setLoadingSearch] = useState(false)

  const searching = searchQuery.trim().length > 0

  useEffect(() => {
    if (!searching || catalogMode !== 'service' || !activeBranchId) {
      setSearchResults([])
      return undefined
    }

    let active = true
    setLoadingSearch(true)

    searchPosServices(searchQuery.trim())
      .then((items) => {
        // API already returns active services only; omit is_active on search payloads.
        if (active) setSearchResults(items)
      })
      .catch(() => {
        if (active) setSearchResults([])
      })
      .finally(() => {
        if (active) setLoadingSearch(false)
      })

    return () => {
      active = false
    }
  }, [searching, catalogMode, activeBranchId, searchQuery])

  const activeCategory = categories.find((category) => category.id === activeCategoryId)
  const serviceSource = searching ? searchResults : services

  const filteredServices = useMemo(
    () => (searching
      ? serviceSource
      : filterCatalogItems(serviceSource, searchQuery, (service) => `${service.name} ${service.code ?? ''} ${service.barcode ?? ''}`)),
    [serviceSource, searchQuery, searching],
  )

  const filteredProducts = useMemo(
    () => filterCatalogItems(products, searchQuery, (product) => `${product.name} ${product.sku ?? ''}`),
    [products, searchQuery],
  )

  const filteredQuickKeys = useMemo(
    () => filterCatalogItems(quickKeys, searchQuery, (key) => getQuickKeyLabel(key)),
    [quickKeys, searchQuery],
  )

  const servicesLoading = catalogMode === 'service'
    && (searching ? loadingSearch : loadingServices)

  const sectionTitle = catalogMode === 'service'
    ? (searching ? 'Search results' : activeCategory?.name ?? 'Services')
    : catalogMode === 'retail'
      ? 'Retail products'
      : 'Quick keys'

  return (
    <section className="pos-v2-picker flex min-h-0 flex-col overflow-hidden border-t border-[#E5E7EB] bg-[#F1F5F9]" aria-label="Catalog items">
      <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] bg-white px-2 py-1">
        <h2 className="truncate text-xs font-bold text-slate-800">{sectionTitle}</h2>
        {catalogMode === 'service' && !searching ? (
          <span className="shrink-0 text-[10px] text-slate-500">
            {filteredServices.length}
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {catalogMode === 'retail' ? (
          <p className="mb-1.5 text-[10px] text-slate-500">
            Scan barcode — wedge scanner switches here automatically.
          </p>
        ) : null}

        {catalogMode === 'service' ? (
          servicesLoading ? (
            <PickerSkeleton />
          ) : filteredServices.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-500">
              {searching ? 'No services match your search.' : 'No services in this category.'}
            </p>
          ) : (
            <div className="pos-v2-item-grid">
              {filteredServices.map((service) => (
                <CatalogServiceCard
                  key={service.id}
                  name={service.name}
                  duration={formatDurationBadge(service.duration)}
                  price={formatServicePrice(service, pricingGender)}
                  disabled={disabled}
                  onClick={() => onServiceSelect?.(service)}
                />
              ))}
            </div>
          )
        ) : catalogMode === 'quick' ? (
          loadingQuickKeys ? (
            <PickerSkeleton />
          ) : filteredQuickKeys.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-500">
              {searching ? 'No quick keys match your search.' : 'No quick keys configured.'}
            </p>
          ) : (
            <div className="pos-v2-item-grid">
              {filteredQuickKeys.map((key) => {
                const price = resolveQuickKeyPrice(key, pricingGender)
                const duration = getQuickKeyDuration(key)

                return (
                  <CatalogServiceCard
                    key={key.id}
                    name={getQuickKeyLabel(key)}
                    duration={duration > 0 ? `${duration} min` : key.item_type}
                    price={`AED ${price.toFixed(0)}`}
                    disabled={disabled}
                    onClick={() => onQuickKeySelect?.(key)}
                  />
                )
              })}
            </div>
          )
        ) : loadingProducts ? (
          <PickerSkeleton />
        ) : filteredProducts.length === 0 ? (
          <p className="py-4 text-center text-xs text-slate-500">
            {searching ? 'No products match your search.' : 'No retail products configured.'}
          </p>
        ) : (
          <div className="pos-v2-item-grid">
            {filteredProducts.map((product) => {
              const price = toAmount(product.sell_price)
              const onHand = Number(product.quantity_on_hand ?? 0)
              const lowStock = onHand <= Number(product.min_stock ?? 0)

              return (
                <CatalogServiceCard
                  key={product.id}
                  name={product.name}
                  duration={lowStock ? `Stock ${onHand.toFixed(0)} · low` : `Stock ${onHand.toFixed(0)}`}
                  price={`AED ${price.toFixed(0)}`}
                  disabled={disabled}
                  onClick={() => onProductSelect?.(product)}
                />
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * @param {object} props
 */
function CatalogServiceCard({ name, duration, price, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="pos-v2-service-card"
    >
      <span className="pos-v2-service-name line-clamp-2">{name}</span>
      {duration ? (
        <span className="pos-v2-service-meta">{duration}</span>
      ) : null}
      <span className="pos-v2-service-price">{price}</span>
    </button>
  )
}

function PickerSkeleton() {
  return (
    <div className="pos-v2-item-grid">
      {Array.from({ length: 12 }).map((_, index) => (
        <div key={index} className="pos-v2-service-card skeleton-shimmer min-h-[4.75rem]" aria-hidden />
      ))}
    </div>
  )
}
